import { createContext, ReactNode, useContext } from "react";
import { RyeMainContentCard } from "./RyeMainContentCard";
import {
  RyeCenteredWidth,
  RyeMainContentHeader,
  ryeCenteredContentClasses,
} from "./RyeMainContentHeader";
import {
  RyeMainContentScrollShadow,
  RyeScrollListener,
  useRyeScrolled,
} from "./RyeMainContentScrollShadow";
import { buildClasses } from "../utils/buildClasses";

/**
 * Controls appended after the header actions of the outermost panel inside the
 * provider, e.g. a modal's close button. Panels nested in that panel's body
 * don't get them.
 */
export const RyeMainContentPanelTrailingActions =
  createContext<ReactNode>(null);

/** A screen or column with a persistent top bar and a scrolling body. */
export function RyeMainContentPanel({
  title,
  leadingActions,
  actions,
  children,
  roundedTl = true,
  roundedTr = true,
  roundedBl = true,
  roundedBr = true,
  padTop = true,
  padBottom = true,
  centerContent = false,
}: {
  title: string;
  /** Controls that sit just after the title, on the left of the top bar. */
  leadingActions?: ReactNode;
  /** Controls on the right of the top bar. */
  actions?: ReactNode;
  children: ReactNode;
  roundedTl?: boolean;
  roundedTr?: boolean;
  roundedBl?: boolean;
  roundedBr?: boolean;
  padTop?: boolean;
  padBottom?: boolean;
  /**
   * Center the body horizontally, with the top bar's title and actions in the
   * same column: 72rem wide at most, or 48rem when "narrow".
   */
  centerContent?: boolean | "narrow";
}): JSX.Element {
  const trailingActions = useContext(RyeMainContentPanelTrailingActions);
  const centeredWidth: RyeCenteredWidth | undefined =
    centerContent === "narrow" ? "narrow" : centerContent ? "wide" : undefined;
  const { scrolled, onScroll } = useRyeScrolled();
  const headerActions =
    actions || trailingActions ? (
      <>
        {actions}
        {trailingActions}
      </>
    ) : undefined;

  return (
    <div
      className={buildClasses(
        "flex",
        "flex-col",
        "flex-1",
        "h-full",
        "min-h-0",
        "min-w-0",
      )}
    >
      <RyeMainContentHeader
        title={title}
        roundedTl={roundedTl}
        roundedTr={roundedTr}
        centered={centeredWidth}
      >
        {leadingActions && (
          <div className={buildClasses("flex", "items-center", "gap-3")}>
            {leadingActions}
          </div>
        )}
        {headerActions && (
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-end",
              "gap-3",
              "ml-auto",
            )}
          >
            {headerActions}
          </div>
        )}
      </RyeMainContentHeader>
      <RyeMainContentScrollShadow visible={scrolled} centered={centeredWidth} />
      <RyeMainContentCard
        roundedTl={false}
        roundedTr={false}
        roundedBl={roundedBl}
        roundedBr={roundedBr}
        padTop={padTop}
        padBottom={padBottom}
      >
        <RyeScrollListener onScroll={onScroll} />
        <RyeMainContentPanelTrailingActions.Provider value={null}>
          {centeredWidth ? (
            <div className={ryeCenteredContentClasses(centeredWidth)}>
              {children}
            </div>
          ) : (
            children
          )}
        </RyeMainContentPanelTrailingActions.Provider>
      </RyeMainContentCard>
    </div>
  );
}
